import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageScale, setImageScale] = useState(false);

  const featuredPeptides = [
  {
    id: 1,
    name: "BPC-157 Premium",
    tagline: "Advanced Tissue Repair & Recovery",
    description: "Accelerate healing and enhance recovery with our pharmaceutical-grade BPC-157. Clinically proven for tissue regeneration and inflammation reduction.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105ce3fbf-1766470445165.png",
    imageAlt: "Close-up of clear glass vial containing BPC-157 peptide solution with blue gradient lighting and molecular structure overlay in modern laboratory setting",
    benefits: ["Faster Recovery", "Tissue Repair", "Anti-Inflammatory"],
    price: "$49.99",
    badge: "Bestseller"
  },
  {
    id: 2,
    name: "TB-500 Elite",
    tagline: "Ultimate Performance Enhancement",
    description: "Maximize your athletic potential with TB-500. Promotes muscle growth, endurance, and accelerated recovery for peak performance.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105ce3fbf-1766470445165.png",
    imageAlt: "Futuristic medical vial with glowing purple peptide solution surrounded by holographic molecular structures and digital data streams in high-tech laboratory environment",
    benefits: ["Muscle Growth", "Enhanced Endurance", "Quick Recovery"],
    price: "$59.99",
    badge: "New Arrival"
  },
  {
    id: 3,
    name: "Ipamorelin Pro",
    tagline: "Growth Hormone Optimization",
    description: "Unlock natural growth hormone production with Ipamorelin. Supports lean muscle development, fat loss, and improved sleep quality.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c17d0d3b-1766470445567.png",
    imageAlt: "Sleek pharmaceutical vial with cyan-colored peptide solution on reflective surface with neon blue and pink lighting creating futuristic Web3 aesthetic atmosphere",
    benefits: ["GH Stimulation", "Fat Loss", "Better Sleep"],
    price: "$44.99",
    badge: "Top Rated"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredPeptides?.length);
      setImageScale(true);
      setTimeout(() => setImageScale(false), 500);
    }, 5000);

    return () => clearInterval(interval);
  }, [featuredPeptides?.length]);

  const handleSlideChange = (index) => {
    setCurrentSlide(index);
  };

  const handleProductClick = (productId) => {
    navigate(`/product-detail-page?id=${productId}`);
  };

  const currentProduct = featuredPeptides?.[currentSlide];

  return (
    <div className="relative w-full min-h-[600px] md:min-h-[700px] lg:min-h-[800px] overflow-hidden">
      {/* Animated Background - Sleeker gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#334155]">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.2),transparent_50%)]"></div>
          <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(124,58,237,0.2),transparent_50%)]"></div>
        </div>
        
        {/* Geometric Pattern Overlay - More subtle */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(14,165,233,0.1) 35px, rgba(14,165,233,0.1) 70px)`
        }}></div>
      </div>
      {/* Content Container */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Content */}
          <div className="space-y-6 md:space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[rgba(14,165,233,0.15)] to-[rgba(124,58,237,0.15)] border border-[rgba(14,165,233,0.4)] backdrop-blur-sm">
              <Icon name="Sparkles" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-[var(--color-primary)]">{currentProduct?.badge}</span>
            </div>

            {/* Title */}
            <div className="space-y-3 md:space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">
                  {currentProduct?.name}
                </span>
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl text-[var(--color-foreground)] font-medium">
                {currentProduct?.tagline}
              </p>
            </div>

            {/* Description */}
            <p className="text-base md:text-lg text-[var(--color-muted-foreground)] leading-relaxed max-w-xl">
              {currentProduct?.description}
            </p>

            {/* Benefits */}
            <div className="flex flex-wrap gap-3">
              {currentProduct?.benefits?.map((benefit, index) =>
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(14,165,233,0.1)] border border-[rgba(14,165,233,0.3)] backdrop-blur-sm">
                
                  <Icon name="Check" size={16} color="var(--color-success)" />
                  <span className="text-sm font-medium text-[var(--color-foreground)]">{benefit}</span>
                </div>
              )}
            </div>

            {/* Price & CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4">
              <div className="space-y-1">
                <p className="text-sm text-[var(--color-muted-foreground)]">Starting at</p>
                <p className="text-3xl md:text-4xl font-bold text-[var(--color-primary)]">{currentProduct?.price}</p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ShoppingCart"
                  iconPosition="left"
                  onClick={() => handleProductClick(currentProduct?.id)}
                  className="flex-1 sm:flex-none">
                  
                  Shop Now
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Info"
                  onClick={() => handleProductClick(currentProduct?.id)}
                  className="flex-1 sm:flex-none">
                  
                  Learn More
                </Button>
              </div>
            </div>
          </div>

          {/* Right Content - Product Showcase */}
          <div className="relative">
            {/* Main Product Display */}
            <div className="relative w-full max-w-[500px] mx-auto aspect-square rounded-3xl overflow-hidden" style={{
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              border: '2px solid rgba(14, 165, 233, 0.3)',
              boxShadow: '0 25px 50px rgba(14, 165, 233, 0.2), 0 0 80px rgba(124, 58, 237, 0.15)'
            }}>
              {/* Blurred Vial Background Layer - 75% blur */}
              <div className="absolute inset-0">
                <Image
                  src="/assets/images/injection-bottle-glass-vial-with-blank-white-label_53876-165420-1770528727675.jpg"
                  alt=""
                  className="w-full h-full object-cover blur-sm scale-110" />
                
                <div className="absolute inset-0 bg-black/40"></div>
              </div>
              
              {/* INF Logo Overlay - Prominent */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20">
                <img
                  src="/assets/images/Copy_of_Black_White_Modern_Handwritten_Square_Studio_Logo-removebg-preview-1770507171149.png"
                  alt="INF LABS"
                  className="w-56 h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 object-contain opacity-50 drop-shadow-2xl" />
                
              </div>
              
              {/* Glow Effect - Sleeker */}
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] opacity-15 blur-3xl rounded-full"></div>
              
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,15,0.8)] via-transparent to-transparent"></div>
              
              {/* Floating Info Card - More refined */}
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded-xl bg-[rgba(30,41,59,0.95)] backdrop-blur-md border border-[rgba(14,165,233,0.4)] shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[var(--color-muted-foreground)] mb-1">Lab Tested</p>
                    <p className="text-xl font-bold text-[var(--color-primary)]">99.9%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[var(--color-muted-foreground)] mb-1">In Stock</p>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[var(--color-success)] animate-pulse"></div>
                      <p className="text-sm font-medium text-[var(--color-success)]">Available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements - Sleeker */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[var(--color-primary)] rounded-full opacity-20"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 border-2 border-[var(--color-secondary)] rounded-full opacity-15"></div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-3 mt-8 lg:mt-12">
          {featuredPeptides?.map((_, index) =>
          <button
            key={index}
            onClick={() => handleSlideChange(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
            index === currentSlide ?
            'w-12 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] shadow-lg shadow-primary/50' :
            'w-2 bg-[rgba(14,165,233,0.3)] hover:bg-[rgba(14,165,233,0.5)]'}`
            }
            aria-label={`Go to slide ${index + 1}`} />

          )}
        </div>
      </div>
      {/* Trust Signals */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[rgba(15,23,42,0.98)] to-transparent py-6 md:py-8 backdrop-blur-sm">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 lg:gap-12">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <span className="text-sm text-[var(--color-muted-foreground)]">SSL Secured</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Award" size={20} color="var(--color-primary)" />
              <span className="text-sm text-[var(--color-muted-foreground)]">Lab Tested</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Truck" size={20} color="var(--color-accent)" />
              <span className="text-sm text-[var(--color-muted-foreground)]">Fast Shipping</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={20} color="var(--color-secondary)" />
              <span className="text-sm text-[var(--color-muted-foreground)]">Secure Payment</span>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default HeroSection;